import {ReactNode, useRef, MutableRefObject, Fragment} from 'react';
import {Box, Link, Skeleton, Stack, SxProps, Typography} from '@mui/material';
import {Theme} from '@mui/system';
import {MdArrowBackIos, MdArrowForwardIos} from 'react-icons/md';
import {ComboActions, ComboActionsProps} from '../combo-actions/combo-actions';
import {useObserveViewport} from '../../hooks';
import {useHistory} from 'react-router-dom';

interface GenericHeaderProps {
  isLoading: boolean;
  pageTitle?: string;
  titleCrumbs?: {label?: string; link: string}[];
  showBackButton?: boolean;
  leftColumn: ReactNode;
  rightColumn?: ReactNode;
  actions?: ComboActionsProps;
  additionalHeight?: number;
  sx?: SxProps<Theme>;
}

const GenericHeader = ({
  isLoading,
  pageTitle,
  titleCrumbs,
  showBackButton = false,
  leftColumn,
  rightColumn,
  actions,
  additionalHeight,
}: GenericHeaderProps) => {
  const titleRef = useRef() as MutableRefObject<HTMLSpanElement>;

  const {isRunningMobile} = useObserveViewport();

  const history = useHistory();
  const showBackButtonBeforeTitle = showBackButton && (!titleCrumbs || !isRunningMobile);
  const backButton = (
    <Box
      component="span"
      sx={{
        height: '38px',
        width: '38px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={() => history.goBack()}
    >
      <MdArrowBackIos color="white" fontSize="22px" />
    </Box>
  );

  const contentColumnsSX: SxProps<Theme> = {
    height: '100%',
    justifyContent: 'flex-end',
  };

  const linksSx: SxProps<Theme> = {
    position: 'relative',
    cursor: 'pointer',
    '&:before': {
      content: "''",
      position: 'absolute',
      height: '2px',
      bottom: '5px',
      left: 0,
      width: '0%',
      transition: 'width 150ms ease-in-out',
      background: 'var(--dcm-white-base)',
    },
    '&:hover': {
      '&:before': {
        width: '100%',
        transition: 'width 150ms ease-in-out',
      },
    },
  };

  const TypographyStyles: SxProps<Theme> = {
    color: 'white',
    maxWidth: `calc(100% - 140px)`,
    position: 'absolute',
    top: 0,
    left: 0,
    margin: 0,
    visibility: isLoading ? 'hidden' : 'visible',
  };

  const TypographyTitle: SxProps<Theme> = {
    ...TypographyStyles,
    fontSize: {
      xs: '18px',
      md: '24px',
    },
    fontWeight: 600,
    lineHeight: '28px',
  };

  const TypographyTitleWithBackButton: SxProps<Theme> = {
    ...TypographyTitle,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const TypographyCrumbs: SxProps<Theme> = {
    ...TypographyStyles,
    fontSize: {
      xs: '18px',
      md: '28px',
    },
    fontWeight: 400,
    lineHeight: '18px',
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: 'linear-gradient(117.57deg, #2a84d6 17.15%, #46b0dd 82.85%), #d9d9d9',
        borderRadius: '5px',
        height: 108 + (additionalHeight || 0) + 'px',
        width: '100%',
        padding: '14px',
      }}
    >
      {/* TITLE / BREADCRUMBS */}
      <Stack
        sx={{
          position: 'relative',
          justifyContent: titleCrumbs ? 'flex-start' : 'center',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          zIndex: 1,
        }}
        flexDirection="row"
      >
        <Typography
          ref={titleRef}
          sx={
            titleCrumbs
              ? TypographyCrumbs
              : showBackButton
                ? TypographyTitleWithBackButton
                : TypographyTitle
          }
          noWrap
        >
          {showBackButtonBeforeTitle && backButton}
          {pageTitle}

          {titleCrumbs &&
            !isRunningMobile &&
            titleCrumbs.map(({label, link}, index) => {
              const isLast = index === titleCrumbs.length - 1;
              return (
                <Fragment key={`title_crumb_${index}`}>
                  {index !== 0 && (
                    <Box
                      component="span"
                      sx={{
                        height: '38px',
                        width: '38px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <MdArrowForwardIos color="white" fontSize="22px" />
                    </Box>
                  )}
                  <Link
                    color="inherit"
                    onClick={() => history.push(link)}
                    sx={{
                      maxWidth: isLast ? 'none' : '300px! important',

                      ...(!isLast && {...linksSx}),
                    }}
                    noWrap
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: isLast ? '28px' : '24px',
                        fontWeight: isLast ? 500 : 400,
                        color: 'white',
                      }}
                      noWrap
                    >
                      {label}
                    </Typography>
                  </Link>
                </Fragment>
              );
            })}

          {titleCrumbs && isRunningMobile && (
            <>
              {backButton}
              <Typography
                component="span"
                sx={{
                  fontSize: '28px',
                  fontWeight: 500,
                  color: 'white',
                }}
                noWrap
              >
                {titleCrumbs[0].label}
              </Typography>
            </>
          )}
        </Typography>
        <Skeleton
          sx={{position: 'absolute', left: 0, top: 0, visibility: isLoading ? 'visible' : 'hidden'}}
          variant="rounded"
          width={titleRef.current?.getBoundingClientRect().width || 100}
          height={titleRef.current?.getBoundingClientRect().height || 25}
        />
      </Stack>

      {/* CONTENT */}
      <Stack
        sx={{
          position: 'relative',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          height: '100%',
          justifyContent: 'space-between',
        }}
        flexDirection="row"
      >
        {leftColumn && (
          <Stack sx={{...contentColumnsSX, alignItems: 'flex-start'}}>{leftColumn}</Stack>
        )}
        {rightColumn && (
          <Stack sx={{...contentColumnsSX, alignItems: 'flex-end'}}>{rightColumn}</Stack>
        )}
      </Stack>

      {/* ACTIONS */}
      {actions && (
        <ComboActions
          {...{
            ...actions,
            wrapperSX: {position: 'absolute', top: '14px', right: '15px', color: 'white'},
            triggerSX: {width: '28px', height: '28px'},
          }}
        />
      )}
    </Box>
  );
};

export default GenericHeader;
